import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import { useLanguage } from '../context/LanguageContext';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './Contact.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://orders.fletaime.com';

const TYPES = ['request', 'complaint', 'suggestion'];

const CHANNELS = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    value: '+383 45 937 644',
    href: 'https://wa.me/38345937644',
    icon: 'fa-brands fa-whatsapp',
  },
  {
    key: 'instagram',
    label: 'Instagram',
    value: '@fletaime',
    href: 'https://www.instagram.com/fletaime/',
    icon: 'fa-brands fa-instagram',
  },
  {
    key: 'email',
    label: 'Email',
    value: 'fletaimecom@gmail.com',
    href: 'mailto:fletaimecom@gmail.com',
    icon: 'fa-regular fa-envelope',
  },
];

const EMPTY = { type: 'request', name: '', email: '', phone: '', message: '', website: '' };

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error | tooMany

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        setStatus('tooMany');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setForm(EMPTY);
      setStatus('success');
    } catch (err) {
      console.error('Contact form failed:', err);
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      <Header />

      <section className="contact-hero">
        <div className="contact-hero-glow" aria-hidden="true" />
        <div className="contact-container">
          <span className="contact-eyebrow">{t('contact.eyebrow')}</span>
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.lead')}</p>
        </div>
      </section>

      <section className="contact-body">
        <div className="contact-container contact-grid">
          <div className="contact-channels">
            {CHANNELS.map((c, i) => (
              <a
                key={c.key}
                className={`contact-card contact-card--${c.key}`}
                href={c.href}
                style={{ "--i": i }}
                target={c.key === 'email' ? undefined : '_blank'}
                rel="noreferrer"
              >
                <span className="contact-card-icon"><i className={c.icon} aria-hidden="true" /></span>
                <span className="contact-card-text">
                  <strong>{t(`contact.${c.key}`)}</strong>
                  <span className="contact-card-value">{c.value}</span>
                  <small>{t(`contact.${c.key}Hint`)}</small>
                </span>
                <span className="contact-card-arrow" aria-hidden="true">→</span>
              </a>
            ))}
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <h2>{t('contact.form.title')}</h2>

            <fieldset className="contact-types">
              <legend>{t('contact.form.type')}</legend>
              {TYPES.map((type) => (
                <label key={type} className={form.type === type ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="type"
                    value={type}
                    checked={form.type === type}
                    onChange={update('type')}
                  />
                  {t(`contact.form.type.${type}`)}
                </label>
              ))}
            </fieldset>

            <div className="contact-row">
              <label>
                <span>{t('contact.form.name')}</span>
                <input type="text" required maxLength={100} value={form.name} onChange={update('name')} autoComplete="name" />
              </label>
              <label>
                <span>{t('contact.form.email')}</span>
                <input type="email" required maxLength={200} value={form.email} onChange={update('email')} autoComplete="email" />
              </label>
            </div>

            <label>
              <span>{t('contact.form.phone')}</span>
              <input type="tel" maxLength={40} value={form.phone} onChange={update('phone')} autoComplete="tel" />
            </label>

            <label>
              <span>{t('contact.form.message')}</span>
              <textarea required minLength={5} maxLength={4000} rows={6} value={form.message} onChange={update('message')} />
            </label>

            {/* Honeypot - hidden from people, bots fill it */}
            <input
              type="text"
              name="website"
              className="contact-hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={update('website')}
            />

            <button type="submit" className="contact-submit" disabled={status === 'sending'}>
              {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
            </button>

            {status === 'success' && <p className="contact-msg ok" role="status">{t('contact.form.success')}</p>}
            {status === 'error' && <p className="contact-msg err" role="alert">{t('contact.form.error')}</p>}
            {status === 'tooMany' && <p className="contact-msg err" role="alert">{t('contact.form.tooMany')}</p>}
          </form>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Contact;
